import { QuestionType } from '@/contracts/enums/questionTypes';
import { EditorQuestion } from '@/contracts/models/survey-editor';
import type { TemplateQuestion } from '@/features/survey-builder/components/tabs/DesignTab';
import { isBranchableType } from '@/features/survey-builder/utils/branchingService';
import { useTranslation } from '@/hooks/useTranslation';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import React from 'react';
import { QuestionForm } from './QuestionForm';

export interface QuestionCardProps {
  question: EditorQuestion;
  index: number;
  onUpdate: (updates: Partial<EditorQuestion>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  disabled?: boolean;
  mixerQuestionId?: string;
  isVotingEnabled?: boolean;
  aiModerationEnabled?: boolean;
  onVotingEnabledChange?: (enabled: boolean) => void;
  onAiModerationEnabledChange?: (enabled: boolean) => void;
  isNewlyDuplicated?: boolean;
  lockStructure?: boolean;
  templateQuestion?: TemplateQuestion;
  onToggleBranch?: (enabled: boolean) => void;
  isLastQuestion?: boolean;
  hasValidationError?: boolean;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  index,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  disabled = false,
  mixerQuestionId,
  isVotingEnabled,
  aiModerationEnabled,
  onVotingEnabledChange,
  onAiModerationEnabledChange,
  isNewlyDuplicated = false,
  lockStructure = false,
  templateQuestion,
  onToggleBranch,
  isLastQuestion = false,
  hasValidationError = false,
}) => {
  const { t } = useTranslation();

  const canBranch =
    !lockStructure && !!onToggleBranch && isBranchableType(question.type);
  const branchEnabled = !!question.branchRules;

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ title: e.target.value });
  };

  const questionTypeLabels: Record<string, string> = {
    [QuestionType.Text]: t('editor.questionTypes.text'),
    [QuestionType.Comment]: t('editor.questionTypes.comment'),
    [QuestionType.RadioGroup]: t('editor.questionTypes.radiogroup'),
    [QuestionType.Checkbox]: t('editor.questionTypes.checkbox'),
    [QuestionType.Dropdown]: t('editor.questionTypes.dropdown'),
    [QuestionType.MultiSelectDropdown]: t(
      'editor.questionTypes.multiSelectDropdown'
    ),
    [QuestionType.Rating]: t('editor.questionTypes.rating'),
    [QuestionType.Matrix]: t('editor.questionTypes.matrix'),
    [QuestionType.Boolean]: t('editor.questionTypes.boolean'),
    [QuestionType.Mixer]: t('editor.questionTypes.mixer'),
  };
  const questionTypeLabel = questionTypeLabels[question.type] || question.type;

  return (
    <div
      className={`border rounded-lg bg-white mb-4 ${
        hasValidationError ? 'border-red-400' : 'border-gray-200'
      } ${
        isNewlyDuplicated
          ? 'expand-in-animation ring-2 ring-blue-400 ring-opacity-75'
          : ''
      }`}
    >
      {/* Header with inline title input and reorder buttons */}
      <div
        className={`flex ${lockStructure ? 'items-start' : 'items-center'} gap-3 p-4 border-b border-gray-200`}
      >
        <span
          className={`text-gray-600 font-medium flex-shrink-0 flex items-center gap-1 ${lockStructure ? 'mt-7' : ''}`}
        >
          {hasValidationError && (
            <i
              className='pi pi-exclamation-triangle text-red-500 text-base'
              aria-label={t('editor.validation.questionHasErrors')}
              title={t('editor.validation.questionHasErrors')}
            />
          )}
          {index + 1}.
        </span>
        <div className='flex-1'>
          {lockStructure && (
            <div className='flex items-center justify-between mb-1'>
              {templateQuestion?.title ? (
                <span className='text-xs text-gray-400'>
                  {t('editor.template.original')} {templateQuestion.title}
                </span>
              ) : (
                <span />
              )}
              <Tag
                value={questionTypeLabel}
                severity='info'
                className='text-xs'
              />
            </div>
          )}
          <InputText
            value={question.title || ''}
            onChange={handleTitleChange}
            placeholder={
              templateQuestion?.title ||
              t('editor.fields.questionTitlePlaceholder')
            }
            disabled={disabled}
            aria-label={t('editor.fields.questionTitleNumberedAriaLabel', {
              number: index + 1,
            })}
            className='w-full font-medium'
          />
        </div>

        {/* Enable Branch toggle — only for branchable question types */}
        {canBranch && (
          <div className='flex items-center gap-2 flex-shrink-0'>
            <InputSwitch
              inputId={`enable-branch-${question.id}`}
              checked={branchEnabled}
              onChange={e => onToggleBranch?.(e.value ?? false)}
              disabled={disabled}
              aria-label={t('editor.branching.enableBranchAriaLabel', {
                number: index + 1,
              })}
            />
            <label
              htmlFor={`enable-branch-${question.id}`}
              className='text-sm text-gray-600 cursor-pointer whitespace-nowrap'
            >
              {t('editor.branching.enableBranch')}
            </label>
          </div>
        )}

        {!lockStructure && (
          <div className='flex items-center gap-1 flex-shrink-0 mr-1'>
            <button
              onClick={onMoveUp}
              disabled={disabled || !onMoveUp}
              aria-label={t('editor.actions.moveUp')}
              title={t('editor.actions.moveUp')}
              className={`p-1 text-gray-400 hover:text-gray-600 transition-colors ${
                disabled || !onMoveUp
                  ? 'opacity-30 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <i className='pi pi-chevron-up text-xs' />
            </button>
            <button
              onClick={onMoveDown}
              disabled={disabled || !onMoveDown}
              aria-label={t('editor.actions.moveDown')}
              title={t('editor.actions.moveDown')}
              className={`p-1 text-gray-400 hover:text-gray-600 transition-colors ${
                disabled || !onMoveDown
                  ? 'opacity-30 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <i className='pi pi-chevron-down text-xs' />
            </button>
          </div>
        )}
      </div>

      {/* Editor Content - Always visible */}
      <div className='p-4'>
        <QuestionForm
          question={question}
          onChange={onUpdate}
          onDelete={lockStructure ? undefined : onDelete}
          onDuplicate={lockStructure ? undefined : onDuplicate}
          disabled={disabled}
          lockStructure={lockStructure}
          templateQuestion={templateQuestion}
          questionNumber={index + 1}
          isLastQuestion={isLastQuestion}
          mixerQuestionId={mixerQuestionId}
          isVotingEnabled={isVotingEnabled}
          aiModerationEnabled={aiModerationEnabled}
          onVotingEnabledChange={onVotingEnabledChange}
          onAiModerationEnabledChange={onAiModerationEnabledChange}
        />
      </div>
    </div>
  );
};
